import { cameraSrc, type BayCamera } from './cameras';
import { analyzeFrame, looksOffline, pictureHeight } from './lib/cameraStatus';

export type CameraState = 'unknown' | 'online' | 'offline' | 'unreachable';

const status = new Map<string, CameraState>();

export function cameraState(id: string): CameraState {
  return status.get(id) ?? 'unknown';
}

/**
 * Works out whether a camera is actually showing anything.
 *
 * Only still-image cameras can be checked: the Crissy feeds are cross-origin
 * iframes, and there is no way to see inside one. Those stay `unknown` rather
 * than being guessed at.
 */
export async function probeCamera(camera: BayCamera): Promise<CameraState> {
  if (!camera.imageUrl) {
    status.set(camera.id, 'unknown');
    return 'unknown';
  }

  const state = await new Promise<CameraState>((resolve) => {
    const img = new Image();
    // Caltrans allows it, and without this the canvas would be tainted.
    img.crossOrigin = 'anonymous';
    img.onerror = () => resolve('unreachable');
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return resolve('unknown');

        ctx.drawImage(img, 0, 0);
        const h = pictureHeight(canvas.height);
        const { data } = ctx.getImageData(0, 0, canvas.width, h);
        const stats = analyzeFrame(data, canvas.width * h);
        resolve(looksOffline(stats) ? 'offline' : 'online');
      } catch {
        // A tainted canvas or a blocked read should not be read as an outage.
        resolve('unknown');
      }
    };
    img.src = cameraSrc(camera);
  });

  status.set(camera.id, state);
  return state;
}

/** Probes every still camera, calling back as each resolves. */
export function probeAll(cameras: BayCamera[], onChange: () => void): void {
  for (const camera of cameras) {
    if (!camera.imageUrl) continue;
    void probeCamera(camera).then(onChange);
  }
}
